import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { HelpHint } from "@/components/HelpHint";
import { Plus, Trash2, Pencil, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";

export default function HelpArticlesCard() {
  const blank = { title: "", category: "Getting started", summary: "", roles: ["admin", "hr_admin", "manager", "employee"], icon: "BookOpen", body: [{ h: "", p: "" }] };
  const ROLES = [["employee", "Employee"], ["manager", "Manager"], ["hr_admin", "HR admin"], ["admin", "Admin"]];
  const CATS = ["Getting started", "Leave & absence", "Benefits", "Learning", "Wellbeing", "People & records", "Recruitment", "Reports & admin"];
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/help/articles").then((r) => setRows(r.data.articles || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const openNew = () => { setEditing(null); setF(blank); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setF({ ...blank, ...a, body: a.body?.length ? a.body : [{ h: "", p: "" }] }); setOpen(true); };
  const toggleRole = (r) => setF((s) => ({ ...s, roles: s.roles.includes(r) ? s.roles.filter((x) => x !== r) : [...s.roles, r] }));
  const setSection = (i, k, v) => setF((s) => ({ ...s, body: s.body.map((b, j) => j === i ? { ...b, [k]: v } : b) }));
  const save = async () => {
    if (!f.title.trim()) { toast.error("Enter a title"); return; }
    if (!f.roles.length) { toast.error("Pick at least one audience"); return; }
    const payload = { ...f, body: f.body.filter((b) => b.h || b.p) };
    try {
      if (editing) await api.put(`/help/articles/${editing.id}`, payload);
      else await api.post("/help/articles", payload);
      toast.success(editing ? "Article updated" : "Article added"); setOpen(false); load();
    } catch { toast.error("Could not save article"); }
  };
  const del = async (a) => { if (!window.confirm(`Delete "${a.title}"?`)) return; try { await api.delete(`/help/articles/${a.id}`); load(); } catch { toast.error("Could not delete"); } };
  const regenerate = async () => {
    if (!window.confirm("Re-sync the built-in help articles to the latest version? Your custom articles are kept.")) return;
    setBusy(true);
    try { const r = await api.post("/help/articles/regenerate"); toast.success(`Help refreshed — ${r.data.added} added, ${r.data.updated} updated.`); load(); }
    catch { toast.error("Could not regenerate"); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="help-articles-card">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-2 flex-wrap">
        <div><h3 className="font-semibold text-[#0B1A2F]">Help articles</h3><p className="text-xs text-slate-400">Shown in the Help centre, filtered by audience.</p></div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={busy} onClick={regenerate} data-testid="help-regenerate-btn"><ArrowRightLeft size={14} className="mr-1" />Restore/refresh defaults</Button>
          <Button size="sm" className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" onClick={openNew} data-testid="help-add-btn"><Plus size={14} className="mr-1" />Add article</Button>
        </div>
      </div>
      <div className="divide-y divide-slate-50">
        {rows.map((a) => (
          <div key={a.id} className="flex items-center justify-between gap-3 px-5 py-3" data-testid={`help-row-${a.id}`}>
            <div className="min-w-0">
              <p className="text-sm font-medium text-[#0B1A2F] truncate">{a.title}{a.custom && <span className="ml-2 text-[10px] bg-sky-50 text-[#2EA3FD] border border-sky-200 rounded px-1.5 py-0.5">Custom</span>}</p>
              <p className="text-xs text-slate-400 truncate">{a.category} · {(a.roles || []).join(", ")}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button variant="ghost" size="sm" className="h-7 text-slate-500 hover:text-[#2EA3FD]" onClick={() => openEdit(a)} data-testid={`help-edit-${a.id}`}><Pencil size={14} /></Button>
              <Button variant="ghost" size="sm" className="h-7 text-rose-600" onClick={() => del(a)} data-testid={`help-del-${a.id}`}><Trash2 size={14} /></Button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="help-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit article" : "Add article"}</DialogTitle>
            <DialogDescription>Articles appear in the Help centre for the audiences you choose.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-slate-500">Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="help-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-500">Category</Label>
                <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                  <SelectTrigger data-testid="help-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-slate-500">Icon</Label>
                <Select value={f.icon} onValueChange={(v) => setF({ ...f, icon: v })}>
                  <SelectTrigger data-testid="help-icon-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["BookOpen", "Rocket", "Gift", "CalendarDays", "GraduationCap", "HeartPulse", "Users", "Briefcase", "ShieldCheck", "BarChart3"].map((ic) => <SelectItem key={ic} value={ic}>{ic}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs text-slate-500">Summary</Label><Input value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} /></div>
            <div>
              <Label className="text-xs text-slate-500 flex items-center gap-1">Audience <HelpHint text="Choose who can see this article in the Help centre. Employees never see admin-only topics." testid="help-audience-hint" /></Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {ROLES.map(([r, lbl]) => (
                  <button key={r} type="button" onClick={() => toggleRole(r)} data-testid={`help-role-${r}`}
                    className={`text-xs rounded-full px-3 py-1 border ${f.roles.includes(r) ? "bg-[#2EA3FD] text-white border-transparent" : "bg-white text-slate-600 border-slate-200"}`}>{lbl}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Sections</Label>
              <div className="space-y-2 mt-1">
                {f.body.map((s, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 p-2 space-y-1.5" data-testid={`help-section-${i}`}>
                    <Input value={s.h} onChange={(e) => setSection(i, "h", e.target.value)} placeholder="Section heading" className="h-8 text-sm" />
                    <Textarea value={s.p} onChange={(e) => setSection(i, "p", e.target.value)} placeholder="Section text" rows={2} />
                    {f.body.length > 1 && <button type="button" onClick={() => setF((st) => ({ ...st, body: st.body.filter((_, j) => j !== i) }))} className="text-xs text-rose-600">Remove section</button>}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => setF((s) => ({ ...s, body: [...s.body, { h: "", p: "" }] }))} data-testid="help-add-section"><Plus size={13} className="mr-1" />Add section</Button>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" onClick={save} data-testid="help-save-btn">{editing ? "Save changes" : "Add article"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
